import React, { useEffect, useRef, useState } from 'react';
import { Control, Path, useController } from 'react-hook-form';
import format from 'date-fns/format';
import isFunction from 'lodash/isFunction';
import { dateFormats } from 'app/constants';
import { Box } from '@chakra-ui/react';
import DatePicker from './DatePicker';
import { getErrorMessage, getInitialMask, isValidDate } from './utils';
import { DateFieldInputMask } from './DateFiedInputMask';

export interface DateFieldProps<T> {
  name: Path<T>;
  control: Control<T>;
  dateFormat?: string;
  placeholder?: string;
  dataTestId?: string;
  label?: string;
  isRequired?: boolean;
  shouldSaveInvalidDate?: boolean;
  onChange?: (date: Date) => void;
  tooltip?: string;
}

export const DateField = <T extends {}>({
  control,
  name,
  placeholder,
  dataTestId,
  dateFormat = dateFormats.DATE_PICKER_FOCUSED,
  label,
  isRequired,
  shouldSaveInvalidDate,
  onChange,
  tooltip,
}: DateFieldProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const iconRef = useRef<SVGSVGElement>(null);
  const { field, fieldState } = useController({
    name,
    control,
  });
  const [maskValue, setMaskValue] = useState(
    getInitialMask(field.value as Date)
  );
  const [showYearsPicker, setShowYearsPicker] = useState(false);
  const isValidMask = isValidDate(maskValue);
  const shouldShowFocusedMask = isFocused || (!isFocused && !isValidMask);
  const errorMessage =
    fieldState.error?.message ||
    getErrorMessage(maskValue, { isRequired, isFocused });

  useEffect(() => {
    const isValidDateObject = isValidDate(field.value as Date);
    if (shouldShowFocusedMask) {
      setMaskValue(
        isValidDateObject ? format(field.value as Date, dateFormat) : maskValue
      );
    } else if (field.value && isValidDateObject) {
      setMaskValue(
        format(field.value as Date, dateFormats.DATE_PICKER_UNFOCUSED)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowFocusedMask, field.value]);

  useEffect(() => {
    const currentMonthText = document.getElementsByClassName(
      'react-datepicker__current-month'
    )[0] as HTMLElement;

    const yearHeaderText = document.getElementsByClassName(
      'react-datepicker-year-header'
    )[0] as HTMLElement;

    if (currentMonthText) {
      currentMonthText.onclick = () => {
        setShowYearsPicker(true);
      };
    }

    if (yearHeaderText) {
      yearHeaderText.onclick = () => {
        setShowYearsPicker(false);
      };
    }
  }, [isOpen, showYearsPicker]);

  return (
    <DatePicker
      fixedHeight
      boxProps={{
        onBlur: () => setIsFocused(false),
      }}
      onFocus={() => setIsFocused(true)}
      open={isOpen}
      showYearPicker={showYearsPicker}
      scrollableYearDropdown
      calendarStartDay={1}
      yearItemNumber={24}
      onClickOutside={(e) => {
        if (e.target !== iconRef.current) {
          setIsOpen(false);
          setShowYearsPicker(false);
        }
      }}
      dateFormat={dateFormat}
      onChange={(date: Date) => {
        field.onChange(new Date(date));
        if (!showYearsPicker) {
          setIsOpen(false);
        }
        setShowYearsPicker(false);

        if (isFunction(onChange)) {
          onChange(date);
        }
      }}
      selected={
        isValidDate(field.value as string) ? (field.value as Date) : null
      }
      customInput={
        <Box _hover={{ svg: { opacity: 1 } }}>
          <DateFieldInputMask
            field={field}
            label={label}
            name={name}
            maskValue={maskValue}
            shouldShowFocusedMask={shouldShowFocusedMask}
            shouldSaveInvalidDate={shouldSaveInvalidDate}
            setMaskValue={setMaskValue}
            placeholder={placeholder}
            dataTestId={dataTestId}
            errorMessage={errorMessage}
            isRequired={isRequired}
            setIsOpen={() => setIsOpen(!isOpen)}
            iconRef={iconRef}
            tooltip={tooltip}
          />
        </Box>
      }
    />
  );
};
